<template>
  <header>
    <div class="header__top">
      <div class="container">
        <div class="flex items-center flex-shrink-0">
          <div
            @click="toggleMenu"
            class="burger-menu"
            :class="{ hidden: !isMenuOpen }"
          >
            <svg-icon name="burger" class="burger-menu__icon" />
          </div>

          <router-link :to="{ name: 'home' }" class="logo">
            <img src="./assets/images/logo.svg" alt="logo" />
            <div class="flex flex-col">
              <span class="logo-title">{{ $t("headers.logoTitle") }}</span>
              <span class="logo-subtitle">{{ hostname }}</span>
            </div>
          </router-link>
        </div>

        <div class="sm:hidden">
          <user-profile @click="() => openUserProfile()" />
        </div>

        <div
          class="hidden ml-16 lg:ml-3 xl:ml-16 sm:flex w-full items-center justify-end gap-5"
        >
          <div class="w-full hidden lg:block">
            <prayer-times theme="dark" />
          </div>

          <div class="flex items-center gap-3">
            <region-selector-toggle @click="openRegionSelector" />
            <dropdown-select
              :options="languageOptions"
              :selected="selectedLanguage"
              @update:selected="updateLanguage"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="header__bottom">
      <div class="container">
        <div class="w-full flex items-center justify-between gap-x-4">
          <div class="hidden lg:flex gap-x-4">
            <nav-menu @open-live-streaming="openLiveStreaming" />
            <send-prayer-button class="flex-shrink-0 hidden md:block" />
          </div>
          <div
            class="w-full mx-auto flex items-center gap-x-4 sm:mx-0 lg:hidden"
          >
            <div class="w-full max-w-[450px]">
              <prayer-times />
            </div>
            <send-prayer-button class="flex-shrink-0 hidden md:block" />
          </div>
          <div class="hidden sm:block flex-shrink-0">
            <user-profile @click="() => openUserProfile()" />
          </div>
        </div>
      </div>
    </div>
  </header>

  <div
    class="fixed inset-0 z-50 transition-opacity duration-300"
    :class="{ hidden: !isMenuOpen }"
    @click="closeMenu"
  >
    <div class="absolute inset-0 bg-black/50"></div>
    <div
      class="bg-[#0d1717] absolute top-[64px] left-0 w-full max-h-[calc(100vh-64px)] transition-transform duration-300 p-4 overflow-y-auto"
      :class="{ 'translate-y-[-100%]': !isMenuOpen }"
    >
      <nav-menu @open-live-streaming="openLiveStreaming" />
      <div class="mt-4 md:hidden">
        <send-prayer-button />
      </div>

      <div class="mt-4 mb-20 flex items-center gap-2">
        <region-selector-toggle class="flex-1" @click="openRegionSelector" />
        <dropdown-select
          :options="languageOptions"
          :selected="selectedLanguage"
          @update:selected="updateLanguage"
        />
      </div>
    </div>
  </div>

  <router-view @open-live-streaming="openLiveStreaming" />

  <footer class="footer">
    <div class="footer__top">
      <div class="container">
        <div class="footer__top-content">
          <div class="footer__contact">
            <div class="footer__contact-title">
              <h3>{{ $t("footer.contact") }}</h3>
              <p>{{ $t("headers.logoTitle") }}</p>
            </div>
            <div class="footer__contact-info">
              <div class="footer__contact-item">
                <span class="footer__contact-label">{{
                  $t("additional.addressLabel")
                }}</span>
                <span class="footer__contact-value">{{
                  $t("footer.addressValue")
                }}</span>
              </div>
              <div class="footer__contact-item">
                <span class="footer__contact-label">{{
                  $t("additional.phoneLabel")
                }}</span>
                <span class="footer__contact-value">{{
                  $t("footer.phoneValue")
                }}</span>
              </div>
            </div>
          </div>

          <div class="footer__stat-card">
            <div class="p-5 pr-0">
              <div class="stat-card__label">{{ $t("stats.registered") }}</div>
              <div class="stat-card__number">{{ hajjCount }}</div>
              <div class="stat-card__text">{{ $t("stats.hajji") }}</div>
              <button
                @click="() => $store.dispatch('openPilgrimForm')"
                class="stat-card__button"
              >
                <span>{{ $t("footer.applyButton") }}</span>
              </button>
            </div>
            <img src="@/assets/images/pilgrim.png" alt="pilgrim" />
          </div>
        </div>
      </div>
    </div>

    <div class="footer__middle">
      <div class="container">
        <div class="footer__middle-content">
          <div class="footer__nav">
            <div class="footer__nav-group">
              <h4 class="footer__nav-title">{{ $t("hajj.hajjis") }}</h4>
              <div class="footer__nav-links">
                <router-link :to="{ name: 'hajis' }" class="footer__nav-link">{{
                  $t("hajj.hajjisList")
                }}</router-link>
              </div>
            </div>
            <div class="footer__nav-group">
              <h4 class="footer__nav-title">{{ $t("media.title") }}</h4>
              <div class="footer__nav-links">
                <router-link
                  :to="{ name: 'category_view', params: { slug: 'news' } }"
                  class="footer__nav-link"
                  >{{ $t("hajj.news") }}</router-link
                >
                <router-link
                  :to="{ name: 'category_view', params: { slug: 'articles' } }"
                  class="footer__nav-link"
                  >{{ $t("media.articles") }}</router-link
                >
                <router-link
                  :to="{ name: 'category_view', params: { slug: 'qa' } }"
                  class="footer__nav-link"
                  >{{ $t("media.qa") }}</router-link
                >

                <router-link
                  :to="{ name: 'video_section' }"
                  class="footer__nav-link"
                  >{{ $t("media.videoSection") }}</router-link
                >
                <router-link
                  :to="{ name: 'photo_section' }"
                  class="footer__nav-link"
                  >{{ $t("media.photoGallery") }}</router-link
                >
                <router-link
                  :to="{ name: 'book_section' }"
                  class="footer__nav-link"
                  >{{ $t("media.library") }}</router-link
                >
              </div>
            </div>
            <div class="footer__nav-group">
              <h4 class="footer__nav-title">{{ $t("hajj.hajjUmra") }}</h4>
              <div class="footer__nav-links">
                <router-link
                  :to="{ name: 'holy_places' }"
                  class="footer__nav-link"
                  >{{ $t("hajj.holyPlaces") }}</router-link
                >
                <!-- <router-link
                  :to="{ name: 'category_view', params: { slug: 'hajis' } }"
                  class="footer__nav-link"
                  >3D тур</router-link
                > -->
                <router-link
                  :to="{ name: 'tour_operators' }"
                  class="footer__nav-link"
                  >{{ $t("hajj.tourCompanies") }}</router-link
                >
                <a
                  @click="openLiveStreaming"
                  class="footer__nav-link cursor-pointer"
                  >{{ $t("footer.onlineStreaming") }}</a
                >
              </div>
            </div>
          </div>
          <div class="footer__social">
            <a href="#" class="footer__social-link">
              <!-- <svg-icon name="telegram" /> -->
            </a>
            <a href="#" class="footer__social-link">
              <!-- <svg-icon name="linkedin" /> -->
            </a>
            <a href="#" class="footer__social-link">
              <!-- <svg-icon name="facebook" /> -->
            </a>
            <a href="#" class="footer__social-link">
              <!-- <svg-icon name="whatsapp" /> -->
            </a>
            <a href="#" class="footer__social-link">
              <!-- <svg-icon name="instagram" /> -->
            </a>
          </div>
        </div>
      </div>
    </div>

    <div class="footer__bottom">
      <div class="container">
        <div class="footer__bottom-content">
          <div class="footer__copyright">
            {{ $t("additional.copyrightText") }}
          </div>
          <div class="footer__lang-selector">
            <div class="footer__lang-button">
              <span>Қаз</span>
              <svg-icon name="arrow-down" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </footer>

  <live-streaming v-if="isLiveStreamingOpen" @close="closeLiveStreaming" />
  <region-selector v-if="isRegionSelectorOpen" @close="closeRegionSelector" />
  <auth-form
    v-if="isAuthFormOpen"
    :initial-tab="initialAuthTab"
    @closeAuthForm="() => (isAuthFormOpen = false)"
  />

  <el-drawer
    v-model="isPilgrimFormOpen"
    append-to="body"
    lock-scroll
    :size="drawerSize"
    :with-header="false"
    :before-close="() => (isPilgrimFormOpen = false)"
  >
    <pilgrim-form
      v-if="isPilgrimFormOpen"
      @close="() => (isPilgrimFormOpen = false)"
    />
  </el-drawer>
</template>

<script>
import { defineComponent } from "vue";
import SvgIcon from "@/components/SvgIcon.vue";
import UserProfile from "@/components/UserProfile.vue";
import PrayerTimes from "@/components/PrayerTimes.vue";
import NavMenu from "@/components/NavMenu.vue";
import DropdownSelect from "@/components/DropdownSelect.vue";
import SendPrayerButton from "@/components/SendPrayerButton.vue";
import LiveStreaming from "@/components/LiveStreaming.vue";
import RegionSelector from "@/components/RegionSelector.vue";
import RegionSelectorToggle from "@/components/RegionSelectorToggle.vue";
import AuthForm from "@/components/AuthForm.vue";
import PilgrimForm from "@/components/PilgrimForm.vue";
import { ElDrawer, ElMessage } from "element-plus";
import { hajiService } from "@/services";

let hostname = "Hajj.kz";
if (window.location.hostname.includes("haj.kz")) {
  hostname = "Haj.kz";
}

export default defineComponent({
  components: {
    SvgIcon,
    UserProfile,
    PrayerTimes,
    NavMenu,
    DropdownSelect,
    SendPrayerButton,
    LiveStreaming,
    RegionSelector,
    RegionSelectorToggle,
    AuthForm,
    PilgrimForm,
    ElDrawer,
  },
  data() {
    return {
      isMenuOpen: false,
      selectedCity: "Астана",
      selectedLanguage: "kk",
      isLiveStreamingOpen: false,
      isRegionSelectorOpen: false,
      isAuthFormOpen: false,
      initialAuthTab: "login",
      hostname: hostname,
      windowWidth: window.innerWidth,
      hajjCount: 0,
    };
  },
  provide() {
    return {
      openAuthForm: this.openAuthForm,
    };
  },
  computed: {
    selectedRegion() {
      return this.$store.state.selectedRegion;
    },
    user() {
      return this.$store.state.user;
    },
    isPilgrimFormOpen() {
      return this.$store.state.isPilgrimFormOpen;
    },
    languageOptions() {
      return [
        { value: "kk", label: this.$t("languages.kk") },
        { value: "ru", label: this.$t("languages.ru") },
      ];
    },
    drawerSize() {
      if (this.windowWidth <= 768) {
        return "100%"; // mobile
      } else if (this.windowWidth <= 1024) {
        return "70%"; // tablet
      } else {
        return "40%"; // desktop
      }
    },
  },
  watch: {
    isAuthFormOpen: {
      handler(newVal) {
        if (newVal) {
          document.body.style.overflow = "hidden";
        } else {
          document.body.style.overflow = "auto";
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.$store.dispatch("checkSelectedRegion");
    this.$store.dispatch("getRegions");

    // Инициализация языка
    const savedLocale = localStorage.getItem("locale");
    if (savedLocale) {
      this.selectedLanguage = savedLocale;
      this.$i18n.locale = savedLocale;
    }

    hajiService.getHajis().then((response) => {
      this.hajjCount = response.count;
    });

    // Добавляем обработчик изменения размера окна
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    // Удаляем обработчик события при размонтировании компонента
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    openLiveStreaming() {
      this.isLiveStreamingOpen = true;
    },
    closeLiveStreaming() {
      this.isLiveStreamingOpen = false;
    },
    openRegionSelector() {
      this.isRegionSelectorOpen = true;
    },
    closeRegionSelector() {
      this.isRegionSelectorOpen = false;
    },
    openUserProfile() {
      console.log("openUserProfile", this.user);
      if (this.user) {
        this.$router.push({ name: "profile_info" });
      } else {
        this.isAuthFormOpen = true;
      }
    },
    openAuthForm(tab = "login") {
      this.isAuthFormOpen = true;
      this.initialAuthTab = tab;
    },
    updateLanguage(language) {
      this.selectedLanguage = language;
      this.$i18n.locale = language;
      localStorage.setItem("locale", language);

      // alert current locale
      ElMessage.success(this.$t("messages.languageChanged"));

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
  },
});
</script>

<style scoped lang="scss">
header {
  @apply flex flex-col;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 40;

  @media (max-width: 1023px) {
    position: relative;
  }

  .header__top {
    background-color: #0d1717;
    @apply h-[64px] flex items-center text-white;

    @media (max-width: 1023px) {
      position: fixed;
      top: 0;
      width: 100%;
      z-index: 40;
    }

    .container {
      @apply flex items-center justify-between px-3;

      .burger-menu {
        @apply lg:hidden flex items-center justify-center w-[32px] h-[32px] flex-shrink-0;
        @apply cursor-pointer mr-2;
        .burger-menu__icon {
          @apply w-8 h-8;
        }
      }

      .logo {
        @apply flex items-center flex-shrink-0 gap-2;
        img {
          @apply w-[40px] h-[40px];
        }
        .logo-title {
          font-weight: 800;
          @apply text-[14px] uppercase;
        }
        .logo-subtitle {
          font-weight: 400;
          @apply text-[12px] opacity-60;
        }
      }
    }
  }

  .header__bottom {
    @apply bg-white h-[64px] flex items-center border-0 border-b border-[#E0E0E0];

    .container {
      @apply flex items-center justify-between;
    }
  }
}

footer {
  @apply mt-10;

  .footer__top {
    background-color: #0d1717;
    @apply py-10 pb-0 text-white;

    &-content {
      @apply flex flex-col md:flex-row justify-between gap-8;
    }
  }

  .footer__contact {
    @apply flex flex-col gap-8 flex-1;

    &-title {
      @apply flex flex-col gap-2;

      h3 {
        @apply text-2xl font-bold;
      }

      p {
        @apply text-lg;
      }
    }

    &-info {
      @apply flex flex-col md:flex-row gap-5 md:gap-6;
      @apply flex-wrap;
    }

    &-item {
      @apply flex flex-col gap-1 flex-1 min-w-[280px];
    }

    &-label {
      @apply text-sm opacity-80;
    }

    &-value {
      @apply text-base;
    }
  }

  .footer__stat-card {
    @apply bg-[#1a2626] rounded-2xl relative;
    @apply flex items-end justify-between;

    img {
      @apply max-w-[200px];
    }

    .stat-card__label {
      @apply text-white text-sm font-normal opacity-60;
    }

    .stat-card__number {
      @apply text-white text-[42px] font-bold;
    }

    .stat-card__text {
      @apply text-white text-sm -mt-3;
    }

    .stat-card__button {
      @apply bg-[#FFCA03] rounded-2xl py-3 px-6 mt-3;
      @apply w-[153px] h-[47px] flex items-center justify-center;
      @apply cursor-pointer border-0 transition-all;

      span {
        @apply text-sm text-black font-medium;
      }

      &:hover {
        @apply bg-[#FFD333];
      }
    }
  }

  .footer__middle {
    background-color: #0d1717;
    @apply text-white pt-4;

    &-content {
      @apply border-t border-[#2e3c3c] pt-10;
      @apply flex flex-col lg:flex-row justify-between gap-10;
    }
  }

  .footer__nav {
    @apply flex flex-wrap gap-10 md:gap-14;

    &-group {
      @apply flex flex-col gap-2;
    }

    &-title {
      @apply text-lg font-bold mb-2;
    }

    &-links {
      @apply flex flex-col gap-2;
    }

    &-link {
      @apply text-white/80 hover:text-white text-sm transition-colors;
    }
  }

  .footer__social {
    @apply flex gap-6 flex-shrink-0 items-start;

    &-link {
      @apply text-white;

      svg {
        @apply w-6 h-6;
      }
    }
  }

  .footer__bottom {
    background-color: #0d1717;
    @apply py-0 text-[#777c7c];

    &-content {
      @apply flex flex-col sm:flex-row justify-between items-center gap-4;
    }
  }

  .footer__copyright {
    @apply text-sm;
  }

  .footer__lang-selector {
    @apply flex items-center;
  }

  .footer__lang-button {
    @apply bg-[#1a2626] text-white rounded-2xl px-3 py-2 flex items-center gap-2;

    svg {
      @apply w-4 h-4 transform rotate-180;
    }
  }
}
</style>

<style lang="scss">
body {
  padding-top: 128px;

  @media (max-width: 1023px) {
    padding-top: 64px;
  }
}

.back-navigation {
  @apply font-semibold text-sm leading-6 flex items-center justify-between;
  .back-button {
    @apply flex items-center cursor-pointer text-black;
  }

  .back-icon {
    @apply w-6 h-6 flex items-center justify-center flex-shrink-0  mr-2 bg-gray-200 rounded;
    @apply text-green-600;

    svg {
      @apply text-xs;
    }
  }

  .share-button {
    @apply flex items-center gap-2 text-zinc-900 cursor-pointer p-2 py-1 rounded-lg font-normal;
    &:hover {
      @apply bg-zinc-100;
    }
    &:active {
      @apply bg-zinc-200;
    }
  }
}
</style>
